import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import DistributionChainAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/distribution-chain/distribution-chain.entity";
import { ICreateDistributionChain } from "./interfaces/create-distribution-chain.interface";
import { IUpdateDistributionChain } from "./interfaces/update-distribution-chain.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_CONFIGURATION_URL;
const moduleName = "distribution-chain";

@Injectable({
  providedIn: "root",
})
export class DistributionChainService extends AbstractService<
  DistributionChainAttributes,
  ICreateDistributionChain,
  IUpdateDistributionChain
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<DistributionChainAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<DistributionChainAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
      const headers: Record<string, any> = {};
      // see order-customer-export for more info about this header
      // ctx: stands for context
      if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
      return this.http.get<IPaginationResult<DistributionChainAttributes>>(API_URL, { headers });
  }

  /**
   * Obtiene cadenas de distribución con paginación y filtros.
   *
   * Nota: Solo busca en 1 campo (name), por lo que no requiere OR lógico.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: name
   * Array filters: statusId, channelId, companyId
   */
  findAllWithCount(
    options: IPagination<DistributionChainAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<DistributionChainAttributes>> {

    const hasSearch = !!options.search;

    const query = buildQuery<DistributionChainAttributes>(
      // Condiciones base
      where('_deleted', Ops.eq(false)),

      // Filtros condicionales
      dataToFilter?.companyId
        ? andWhere('company.id' as keyof DistributionChainAttributes, Ops.eq(dataToFilter.companyId))
        : noop(),
      dataToFilter?.channelId && dataToFilter.channelId.length > 0
        ? andWhere('channel.id' as keyof DistributionChainAttributes, Ops.in(...dataToFilter.channelId))
        : noop(),
      dataToFilter?.status && dataToFilter.status.length > 0
        ? andWhere('statusId', Ops.in(...dataToFilter.status))
        : noop(),

      // Búsqueda (solo si hay término)
      hasSearch
        ? andWhere("name", Ops.like(options.search!, "i"))
        : noop(),

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<DistributionChainAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(channel?): Observable<IRequestResult<DistributionChainAttributes[]>> {
    const query = buildQuery<DistributionChainAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      channel? andWhere("channel.id"  as keyof DistributionChainAttributes ,channel) : noop(),
      select(["id","name", "externalKeys","id"]),
      orderBy("name", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<DistributionChainAttributes[]>>(API_URL);
  }
}
